import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { LibraryBig } from '@heidi/design-system/icons';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { BookOpen, Info, MessageSquareText, TextSearch } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { upgradeTrialFeatureListMessages } from '../ninety-day-end-of-trial/upgrade-trial-feature-list-messages';
import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { clinicianTrialEndedModalMessages } from './clinician-trial-ended-modal-messages';

interface ClinicianTrialEndedFeatureListProps {
  /**
   * Cohort branch driving which rows appear:
   * - `clinician`: solo Clinician (PRO → Scribe Plus). 2 rows.
   * - `practice_admin` / `non_admin`: 3 rows (adds "Team guidelines").
   *
   * The section header is shared across all cohorts.
   */
  cohort: 'clinician' | 'practice_admin' | 'non_admin';
}

type SourceRecommendation = {
  domain: string;
  website_name?: string | string[];
};

/**
 * Feature list rendered inside the Clinician/Practice tab of the
 * {@link ClinicianTrialEndedModal}. Static rows (not data-driven from
 * `mapEvidenceUsageToTrialRecapFeatures` like the recap surface — the rows
 * here are what the user *gains by upgrading*, not what they used in the
 * trial). Right-hand widgets stay context-only — no "Try now" CTAs since
 * the modal's whole purpose is the upgrade pitch, not feature discovery.
 *
 * Rows:
 * 1. Answers in patient sessions — info tooltip + "Used Nx" badge driven by
 *    `useTrialRecapUsageStats.usageStats.answers_from_evidence` (team-
 *    aggregated for admins, personal otherwise — handled by the hook).
 *    Gated on `useEvidenceAccess().isInSessionEvidenceShown` — the same
 *    signal the chat / session surfaces use to decide whether to surface
 *    in-session evidence at all. False for denied geographies (UK region,
 *    Japan, and EEA countries), while access is pending, or when the user
 *    lacks Evidence access.
 * 2. Set preferred sources — avatar group of the user's region/speciality
 *    source recommendations from `useQuerySourceRecommendations`.
 * 3. (Practice admin + Practice non-admin) Team guidelines — label only.
 *
 * Figma:
 * - Solo Clinician: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10978-30127
 * - Practice admin: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10978-31030
 * - Non-admin: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10993-35498
 */
export function ClinicianTrialEndedFeatureList({
  cohort,
}: ClinicianTrialEndedFeatureListProps) {
  const intl = useIntl();
  const { StackedSourceAvatars } = getCommerceTrialsUiPorts();
  // Mounting this list implies the parent modal is open, so the hook is
  // unconditionally enabled. Parent gates the modal itself.
  const { usageStats } = getCommerceTrialsUiPorts().useTrialRecapUsageStats(true);
  const answersCount = usageStats?.answers_from_evidence ?? 0;

  // Suppress the in-session-evidence row when the user doesn't have access to
  // the feature in the first place — same gate the chat / session surfaces use.
  // The gate is false for denied geographies (UK region, Japan, and EEA
  // countries), while access is pending, or when the user lacks Evidence access.
  const { isInSessionEvidenceShown } = getCommerceTrialsUiPorts().useEvidenceAccess();

  const { data: recommendationsData } =
    getCommerceTrialsUiPorts().useQuerySourceRecommendations();
  // Prefer `website_name` (human-readable) over `display_name`, which the API
  // returns as a camelCase internal codename. Mirrors `getSourceDisplayName`
  // in `library/source-lists/domainUtils.ts`.
  const sourceAvatars = useMemo(
    () =>
      ((recommendationsData?.sources ?? []) as SourceRecommendation[])
        .slice(0, 3)
        .map((s) => {
          const websiteName = Array.isArray(s.website_name)
            ? s.website_name[0]
            : s.website_name;
          return {
            domain: s.domain,
            displayName: websiteName?.trim() || s.domain,
          };
        }),
    [recommendationsData?.sources]
  );
  const totalSources = recommendationsData?.sources?.length ?? 0;
  const hasMoreSources = totalSources > sourceAvatars.length;

  const showTeamGuidelines = cohort !== 'clinician';

  // Usage badge tracks the tier the user is being pitched into. The Badge
  // component only exposes `proTier` as a first-class variant; for Practice
  // we fall back to `neutral` + the `tier-practice-*` semantic tokens, which
  // is the same workaround used in `TrialRecapFeatureList`.
  const isClinicianCohort = cohort === 'clinician';

  return (
    <div className="flex w-full flex-col gap-2">
      <TypographyP2 className="font-medium text-text-secondary">
        <FormattedMessage {...clinicianTrialEndedModalMessages.featuresHeader} />
      </TypographyP2>

      <ul className="flex w-full flex-col">
        {/* Row 1: Answers in patient sessions (gated on in-session evidence access) */}
        {isInSessionEvidenceShown && (
          <li
            className={cn(
              'flex items-center gap-2 px-2 py-4',
              'border-b border-border last:border-b-0'
            )}
            data-testid="trial-ended-feature-answers-in-patient-sessions"
          >
            <MessageSquareText
              aria-hidden
              className="size-5 shrink-0 text-text-primary"
            />
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <TypographyP className="min-w-0 text-text-primary">
                <FormattedMessage
                  {...clinicianTrialEndedModalMessages.featureAnswersInPatientSessions}
                />
              </TypographyP>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={intl.formatMessage(
                      clinicianTrialEndedModalMessages.evidenceAnswersTooltipAriaLabel
                    )}
                    className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                  >
                    <Info aria-hidden />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-start">
                  <FormattedMessage
                    {...clinicianTrialEndedModalMessages.evidenceAnswersTooltip}
                  />
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </div>
            {answersCount > 0 && (
              <Badge
                color={isClinicianCohort ? 'proTier' : 'neutral'}
                size="sm"
                className={cn(
                  'shrink-0 shadow-none',
                  !isClinicianCohort && 'bg-tier-practice-muted text-tier-practice-text'
                )}
                data-testid="trial-ended-feature-usage-badge"
              >
                <FormattedMessage
                  {...clinicianTrialEndedModalMessages.usageBadge}
                  values={{ count: answersCount }}
                />
              </Badge>
            )}
          </li>
        )}

        {/* Row 2: Set preferred sources ─────────────────────────────────── */}
        <li
          className={cn(
            'flex items-center gap-2 px-2 py-4',
            'border-b border-border last:border-b-0'
          )}
          data-testid="trial-ended-feature-set-preferred-sources"
        >
          <TextSearch aria-hidden className="size-5 shrink-0 text-text-primary" />
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <TypographyP className="min-w-0 text-text-primary">
              <FormattedMessage
                {...clinicianTrialEndedModalMessages.featureSetPreferredSources}
              />
            </TypographyP>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={intl.formatMessage(
                    clinicianTrialEndedModalMessages.featureSetPreferredSourcesTooltipAriaLabel
                  )}
                  className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                  data-testid="trial-ended-feature-set-preferred-sources-info"
                >
                  <Info aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-start">
                <FormattedMessage
                  {...clinicianTrialEndedModalMessages.featureSetPreferredSourcesTooltip}
                />
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
          {sourceAvatars.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disableAutomaticTypographyClasses
                  aria-label={intl.formatMessage(
                    clinicianTrialEndedModalMessages.featureSetPreferredSourcesAvatarsTooltipAriaLabel
                  )}
                  className="inline-flex h-auto shrink-0 items-center gap-0 rounded-full p-0 hover:bg-transparent"
                  data-testid="trial-ended-feature-set-preferred-sources-avatars"
                >
                  <StackedSourceAvatars
                    sources={sourceAvatars}
                    size="md"
                    maxVisible={3}
                    disableTooltips
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-start">
                <div className="flex flex-col gap-1">
                  <span>
                    <FormattedMessage
                      {...clinicianTrialEndedModalMessages.featureSetPreferredSourcesAvatarsTooltipIntro}
                    />
                  </span>
                  <ul className="ms-4 list-disc">
                    {sourceAvatars.map(({ domain, displayName }) => (
                      <li key={domain}>{displayName}</li>
                    ))}
                  </ul>
                  {hasMoreSources && (
                    <span>
                      <FormattedMessage
                        {...clinicianTrialEndedModalMessages.featureSetPreferredSourcesAvatarsTooltipMore}
                      />
                    </span>
                  )}
                </div>
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          )}
        </li>

        {/* Row 3: Build an Evidence library ─────────────────────────────
            Per Figma node 11262:37079 — always shown so cohorts without
            in-session Evidence still get two upgrade benefits in the list.
            Messages are shared with the upgrade modal
            (`UpgradeTrialFeatureList`) so both surfaces stay in sync. */}
        <li
          className={cn(
            'flex items-center gap-2 px-2 py-4',
            'border-b border-border last:border-b-0'
          )}
          data-testid="trial-ended-feature-build-evidence-library"
        >
          <LibraryBig aria-hidden size={20} className="shrink-0 text-text-primary" />
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <TypographyP className="min-w-0 text-text-primary">
              <FormattedMessage
                {...upgradeTrialFeatureListMessages.featureBuildEvidenceLibrary}
              />
            </TypographyP>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={intl.formatMessage(
                    upgradeTrialFeatureListMessages.featureBuildEvidenceLibraryTooltipAria
                  )}
                  className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                  data-testid="trial-ended-feature-build-evidence-library-info"
                >
                  <Info aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-start">
                <FormattedMessage
                  {...upgradeTrialFeatureListMessages.featureBuildEvidenceLibraryTooltip}
                />
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
        </li>

        {/* Row 4: Team guidelines (Practice admin only) ─────────────────── */}
        {showTeamGuidelines && (
          <li
            className={cn(
              'flex items-center gap-2 px-2 py-4',
              'border-b border-border last:border-b-0'
            )}
            data-testid="trial-ended-feature-team-guidelines"
          >
            <BookOpen aria-hidden className="size-5 shrink-0 text-text-primary" />
            <TypographyP className="min-w-0 flex-1 text-text-primary">
              <FormattedMessage
                {...clinicianTrialEndedModalMessages.featureTeamGuidelines}
              />
            </TypographyP>
          </li>
        )}
      </ul>
    </div>
  );
}
